import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import BottomNav from "@/components/BottomNav";
import PatientAssistantFab from "@/components/PatientAssistantFab";
import DietitianLayout from "@/components/DietitianLayout";
import DoctorLayout from "@/components/DoctorLayout";
import AdminLayout from "@/components/AdminLayout";
import LandingPage from "@/pages/LandingPage";
import PatientDashboard from "@/pages/PatientDashboard";
import FindDietitian from "@/pages/FindDietitian";
import PatientInbox from "@/pages/PatientInbox";
import TasksPage from "@/pages/TasksPage";
import MealsPage from "@/pages/MealsPage";
import ProfilePage from "@/pages/ProfilePage";
import PaymentPage from "@/pages/PaymentPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SettingsPage from "@/pages/SettingsPage";
import DietitianHome from "@/pages/dietitian/DietitianHome";
import PatientManagement from "@/pages/dietitian/PatientManagement";
import DietitianInbox from "@/pages/dietitian/DietitianInbox";
import DietitianAnalytics from "@/pages/dietitian/DietitianAnalytics";
import DoctorHome from "@/pages/doctor/DoctorHome";
import DoctorInbox from "@/pages/doctor/DoctorInbox";
import DoctorPatients from "@/pages/doctor/DoctorPatients";
import DoctorAnalytics from "@/pages/doctor/DoctorAnalytics";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminOverview from "@/pages/admin/AdminOverview";
import DietitianVerification from "@/pages/admin/DietitianVerification";
import RevenueDashboard from "@/pages/admin/RevenueDashboard";
import PlatformAnalytics from "@/pages/admin/PlatformAnalytics";
import ChatBox from "@/pages/ChatBox";
import WalletPage from "@/pages/WalletPage";
import HealthRecordsPage from "@/pages/HealthRecordsPage";
import ExploreTipsPage from "@/pages/ExploreTipsPage";
import ShareTipsPage from "@/pages/ShareTipsPage";
import NotFound from "./pages/NotFound";
import PatientCallListener from "@/components/PatientCallListener";
import SymptomAssistantPage from "@/pages/SymptomAssistantPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

const GuestRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (user) {
    const homes: Record<string, string> = {
      patient: "/patient-home",
      dietitian: "/dietitian",
      doctor: "/doctor",
      admin: "/admin",
    };
    return <Navigate to={homes[user.role] ?? "/patient-home"} replace />;
  }
  return <>{children}</>;
};

const AppContent = () => {
  const location = useLocation();
  const { user } = useAuth();

  const isPatient =
    location.pathname.startsWith("/patient") ||
    location.pathname === "/find-dietitian" ||
    location.pathname === "/find-doctor" ||
    location.pathname === "/payment" ||
    location.pathname === "/settings" ||
    location.pathname === "/patient-health-records";

  return (
    <>
    {/* PatientCallListener persists across all patient pages so incoming calls from
        practitioners are always received, even when the chat modal is not open */}
    {user?.role === "patient" && <PatientCallListener />}
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
      <Route path="/signup" element={<GuestRoute><SignupPage /></GuestRoute>} />
      <Route path="/admin/login" element={<GuestRoute><AdminLogin /></GuestRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Patient routes */}
      <Route path="/patient-home" element={<ProtectedRoute requiredRole="patient"><div className="max-w-md mx-auto relative min-h-screen"><PatientDashboard /><BottomNav /></div></ProtectedRoute>} />
      <Route path="/find-dietitian" element={<ProtectedRoute requiredRole="patient"><div className="max-w-md mx-auto relative min-h-screen"><FindDietitian /><BottomNav /></div></ProtectedRoute>} />
      <Route path="/find-doctor" element={<ProtectedRoute requiredRole="patient"><div className="max-w-md mx-auto relative min-h-screen"><FindDietitian /><BottomNav /></div></ProtectedRoute>} />
      <Route path="/patient-inbox" element={<ProtectedRoute requiredRole="patient"><div className="max-w-md mx-auto relative min-h-screen"><PatientInbox /><BottomNav /></div></ProtectedRoute>} />
      <Route path="/patient-tasks" element={<ProtectedRoute requiredRole="patient"><div className="max-w-md mx-auto relative min-h-screen"><TasksPage /><BottomNav /></div></ProtectedRoute>} />
      <Route path="/patient-tips" element={<ProtectedRoute requiredRole="patient"><div className="max-w-md mx-auto relative min-h-screen"><ExploreTipsPage /><BottomNav /></div></ProtectedRoute>} />
      <Route path="/patient-symptom-assistant" element={<ProtectedRoute requiredRole="patient"><div className="max-w-md mx-auto relative min-h-screen"><SymptomAssistantPage /><BottomNav /></div></ProtectedRoute>} />
      <Route path="/patient-meals" element={<ProtectedRoute requiredRole="patient"><div className="max-w-md mx-auto relative min-h-screen"><MealsPage /><BottomNav /></div></ProtectedRoute>} />
      <Route path="/patient-profile" element={<ProtectedRoute requiredRole="patient"><div className="max-w-md mx-auto relative min-h-screen"><ProfilePage /><BottomNav /></div></ProtectedRoute>} />
      <Route path="/payment" element={<ProtectedRoute requiredRole="patient"><div className="max-w-md mx-auto relative min-h-screen"><PaymentPage /><BottomNav /></div></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute requiredRole="patient"><div className="max-w-md mx-auto relative min-h-screen"><SettingsPage role="patient" /><BottomNav /></div></ProtectedRoute>} />
      <Route path="/patient-health-records" element={<ProtectedRoute requiredRole="patient"><div className="max-w-md mx-auto relative min-h-screen"><HealthRecordsPage /><BottomNav /></div></ProtectedRoute>} />

      {/* Dietitian routes */}
      <Route path="/dietitian" element={<ProtectedRoute requiredRole="dietitian"><DietitianLayout><DietitianHome /></DietitianLayout></ProtectedRoute>} />
      <Route path="/dietitian/patients" element={<ProtectedRoute requiredRole="dietitian"><DietitianLayout><PatientManagement /></DietitianLayout></ProtectedRoute>} />
      <Route path="/dietitian/inbox" element={<ProtectedRoute requiredRole="dietitian"><DietitianLayout><DietitianInbox /></DietitianLayout></ProtectedRoute>} />
      <Route path="/dietitian/wallet" element={<ProtectedRoute requiredRole="dietitian"><DietitianLayout><WalletPage /></DietitianLayout></ProtectedRoute>} />
      <Route path="/dietitian/analytics" element={<ProtectedRoute requiredRole="dietitian"><DietitianLayout><DietitianAnalytics /></DietitianLayout></ProtectedRoute>} />
      <Route path="/dietitian/share-tips" element={<ProtectedRoute requiredRole="dietitian"><DietitianLayout><ShareTipsPage /></DietitianLayout></ProtectedRoute>} />
      <Route path="/dietitian/chat/:conversationId" element={<ProtectedRoute requiredRole="dietitian"><DietitianLayout><ChatBox practitionerType="dietitian" /></DietitianLayout></ProtectedRoute>} />
      <Route path="/dietitian/settings" element={<ProtectedRoute requiredRole="dietitian"><DietitianLayout><SettingsPage role="dietitian" /></DietitianLayout></ProtectedRoute>} />

      {/* Doctor routes */}
      <Route path="/doctor" element={<ProtectedRoute requiredRole="doctor"><DoctorLayout><DoctorHome /></DoctorLayout></ProtectedRoute>} />
      <Route path="/doctor/patients" element={<ProtectedRoute requiredRole="doctor"><DoctorLayout><DoctorPatients /></DoctorLayout></ProtectedRoute>} />
      <Route path="/doctor/inbox" element={<ProtectedRoute requiredRole="doctor"><DoctorLayout><DoctorInbox /></DoctorLayout></ProtectedRoute>} />
      <Route path="/doctor/wallet" element={<ProtectedRoute requiredRole="doctor"><DoctorLayout><WalletPage /></DoctorLayout></ProtectedRoute>} />
      <Route path="/doctor/analytics" element={<ProtectedRoute requiredRole="doctor"><DoctorLayout><DoctorAnalytics /></DoctorLayout></ProtectedRoute>} />
      <Route path="/doctor/share-tips" element={<ProtectedRoute requiredRole="doctor"><DoctorLayout><ShareTipsPage /></DoctorLayout></ProtectedRoute>} />
      <Route path="/doctor/chat/:conversationId" element={<ProtectedRoute requiredRole="doctor"><DoctorLayout><ChatBox practitionerType="doctor" /></DoctorLayout></ProtectedRoute>} />
      <Route path="/doctor/settings" element={<ProtectedRoute requiredRole="doctor"><DoctorLayout><SettingsPage role="doctor" /></DoctorLayout></ProtectedRoute>} />

      {/* Admin routes */}
      <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminLayout><AdminOverview /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/verification" element={<ProtectedRoute requiredRole="admin"><AdminLayout><DietitianVerification /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/revenue" element={<ProtectedRoute requiredRole="admin"><AdminLayout><RevenueDashboard /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute requiredRole="admin"><AdminLayout><PlatformAnalytics /></AdminLayout></ProtectedRoute>} />

      <Route path="*" element={<NotFound />} />
    </Routes>
      {user?.role === "patient" && isPatient && location.pathname !== "/patient-symptom-assistant" && <PatientAssistantFab />}
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </BrowserRouter>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
